import React, { useEffect, useMemo, useRef, useState } from "react";

import { Box, Typography } from "@mui/material";

import { TURNSTILE_SITE_KEY, isTurnstileEnabled } from "./turnstile";

type TurnstileWidgetProps = {
  action: string;
  onTokenChange: (token: string | null) => void;
  resetKey?: number;
};

type TurnstileRenderOptions = {
  action?: string;
  appearance?: "always" | "execute" | "interaction-only";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  sitekey: string;
  size?: "compact" | "flexible" | "normal";
  theme?: "light";
};

type TurnstileInstance = {
  remove: (widgetId: string) => void;
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions
  ) => string;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileInstance;
  }
}

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";

let loadTurnstilePromise: Promise<TurnstileInstance> | null = null;

const loadTurnstile = async () => {
  if (window.turnstile) {
    return window.turnstile;
  }

  if (!loadTurnstilePromise) {
    loadTurnstilePromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(
        TURNSTILE_SCRIPT_ID
      ) as HTMLScriptElement | null;

      if (existingScript) {
        existingScript.addEventListener("load", () => {
          if (window.turnstile) {
            resolve(window.turnstile);
            return;
          }

          reject(new Error("Turnstile failed to initialize"));
        });
        existingScript.addEventListener("error", () => {
          reject(new Error("Turnstile script failed to load"));
        });

        return;
      }

      const script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.turnstile) {
          resolve(window.turnstile);
          return;
        }

        reject(new Error("Turnstile failed to initialize"));
      };
      script.onerror = () => {
        reject(new Error("Turnstile script failed to load"));
      };

      document.body.appendChild(script);
    });
  }

  return loadTurnstilePromise;
};

function TurnstileWidget(props: TurnstileWidgetProps) {
  const { action, onTokenChange, resetKey = 0 } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isScriptReady, setIsScriptReady] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  const enabled = isTurnstileEnabled();
  const message = useMemo(() => {
    if (hasLoadError) {
      return "Verification failed to load. Please refresh and try again.";
    }

    if (!isScriptReady) {
      return "Loading verification...";
    }

    return "Complete verification to continue.";
  }, [hasLoadError, isScriptReady]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let isMounted = true;

    loadTurnstile()
      .then(() => {
        if (!isMounted) {
          return;
        }

        setIsScriptReady(true);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setHasLoadError(true);
      });

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !isScriptReady || !containerRef.current || widgetIdRef.current) {
      return;
    }

    const turnstile = window.turnstile;

    if (!turnstile) {
      setHasLoadError(true);
      return;
    }

    widgetIdRef.current = turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      action,
      appearance: "interaction-only",
      size: "flexible",
      theme: "light",
      callback: (token: string) => {
        setHasLoadError(false);
        onTokenChange(token);
      },
      "expired-callback": () => {
        onTokenChange(null);
      },
      "error-callback": () => {
        onTokenChange(null);
        setHasLoadError(true);
      },
    });

    return () => {
      if (!widgetIdRef.current || !window.turnstile) {
        return;
      }

      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    };
  }, [action, enabled, isScriptReady, onTokenChange]);

  useEffect(() => {
    if (!enabled || !widgetIdRef.current || !window.turnstile) {
      return;
    }

    onTokenChange(null);
    setHasLoadError(false);
    window.turnstile.reset(widgetIdRef.current);
  }, [enabled, onTokenChange, resetKey]);

  if (!enabled) {
    return null;
  }

  return (
    <Box mt={1.5} display="flex" flexDirection="column" alignItems="center">
      <Box width="100%" maxWidth="320px" ref={containerRef} />
      {hasLoadError ? (
        <Typography
          variant="caption"
          align="center"
          color="textSecondary"
          sx={{ mt: 1 }}
        >
          {message}
        </Typography>
      ) : null}
    </Box>
  );
}

export default TurnstileWidget;
